import React, { useMemo, useRef } from 'react'
import { Avatar, Icon, Input, Modal, Popover, Typography } from 'antd'
import { TableEventListeners } from 'antd/lib/table'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ApplicationState } from 'src/store'
import {
  Approver,
  Checker,
  Controller,
  Gender,
  Id,
  Reason,
  RequestApprovalStatus,
  RequestClosingStatus,
  RequestConferenceStatus,
  RequestEmpoweredUsers,
  RequestUserDataStatus
} from 'src/types'
import { ColumnProps } from 'src/components/Ant/Table'
import { getMyRequests } from 'src/core/store/ducks/requests/actions'
import { InativeRequest, SetRequestErpCode } from 'src/services/api/requests'
import { List, ListItem, ListItemMeta } from 'src/components/Ant'
import { SyncStatusBadge } from 'src/components/atoms/sync-status-badge'
import { TagRole } from 'src/components/molecules'
import UserAvatar from 'src/components/UserAvatar'
import * as Css from './styles'
import UnifiedTag from './components/UnifiedTag'

type Status =
  | RequestApprovalStatus
  | RequestClosingStatus
  | RequestConferenceStatus
  | RequestUserDataStatus

interface User {
  gender: Gender
  name: string
  reason?: Reason
  status: Status
  user_id: Id
}

export interface DataSource {
  cost: string
  extra?: any
  id: Id
  number: Id
  startDate: string
  status: Status
  title: string
  users: User[]
}

interface Props {
  approvals?: boolean
  closings?: boolean
  conferences?: boolean
  currentPage: number
  dataSource: DataSource[]
  loading: boolean
  myRequests?: boolean
  onChange(page: number): void
  onRow?: (record: DataSource, index: number) => TableEventListeners
  totalPages: number
}

interface RenderUsers {
  gender: Gender
  name: string
  user_id: Id
}

interface RenderUserProps {
  users: RenderUsers[]
}

function RenderUserAvatar({ user }: { user: RenderUsers }): JSX.Element {
  return <UserAvatar className="mr-8" gender={user.gender} name={user.name} />
}

function usersMap(user: RenderUsers) {
  return <RenderUserAvatar key={user.user_id} user={user} />
}

function ExcluirRequest({
  organization_id,
  request_id
}: {
  organization_id: Id
  request_id: Id
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  return (
    <Icon
      onClick={e => {
        e.stopPropagation()
        e.preventDefault()
        Modal.confirm({
          cancelText: t('common.actions.no'),
          okText: t('common.actions.yes'),
          onCancel() {},
          onOk: async () => {
            const response = await InativeRequest({
              organization_id,
              request_id
            })

            //@ts-ignore
            const error = response.data?.error

            if (response.status === 200 && !error) {
              dispatch(getMyRequests({ page: 1 }))
            } else if (error) {
              Modal.error({
                title: error
              })
            } else {
              Modal.error({
                title: t('common.response.status-500')
              })
            }
          },
          title: t('web.request.exclude')
        })
      }}
      style={{ fontSize: 22 }}
      theme="twoTone"
      title={t('web.request.exclude-info')}
      twoToneColor="#ff6464"
      type="delete"
    />
  )
}

function RenderIntegracaoModal({
  erp_code,
  organization_id,
  request_id
}: {
  erp_code: string
  organization_id: Id
  request_id: Id
}) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const integrationText = useRef(null)
  const textCollumn = erp_code ?? t('web.integration.insert')
  return (
    <Css.Integration
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()

        const cancelText = t('common.actions.cancel')
        const okText = t('common.actions.send')
        const content = (
          <Input
            defaultValue={erp_code}
            onChange={e => (integrationText.current = e.target.value)}
          />
        )
        const title = t('web.integration.add-erp-code')
        Modal.confirm({
          cancelText,
          content,
          okText,
          onCancel() {},
          onOk: async () => {
            const response = await SetRequestErpCode({
              organization_id,
              request_id,
              erp_code: integrationText.current.trim()
            })

            if (response.status === 200) {
              dispatch(getMyRequests({ page: 1 }))
            } else {
              Modal.error({
                title: t('common.response.status-500')
              })
            }
          },
          title
        })
      }}
    >
      <Typography.Text>{textCollumn}</Typography.Text>
    </Css.Integration>
  )
}

function RenderUsers({ users = [] }: RenderUserProps): JSX.Element {
  if (users.length <= 3) {
    return <>{users.map(usersMap)}</>
  }

  return (
    <>
      {users.slice(0, 2).map(usersMap)}
      <Avatar>+{users.length - 2}</Avatar>
    </>
  )
}

function Table({
  approvals = false,
  closings = false,
  conferences = false,
  currentPage,
  dataSource,
  loading,
  myRequests = false,
  onChange,
  onRow,
  totalPages
}: Props) {
  const {
    me: {
      user: {
        auditor,
        organization_id,
        role_group,
        tenant_id,
        user_id,
        user_level_type
      }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))
  const hasUserRole = Boolean(
    auditor ||
      role_group.approver.length ||
      role_group.checker.length ||
      role_group.controller.length ||
      ['ADMIN', 'SUPER_ADMIN', 'REALM_ADMIN'].includes(user_level_type)
  )

  const canShowExcludeRequest =
    hasUserRole && !(+tenant_id === 877803730250 && +user_id !== 3261)

  const columns: ColumnProps<unknown>[] = useMemo(
    () => [
      {
        title: 'web.table.head.id',
        dataIndex: 'number',
        key: 'number'
      },
      {
        title: 'web.table.head.title',
        dataIndex: 'title',
        key: 'title'
      },
      ...(myRequests && [334081949181].includes(+tenant_id)
        ? [
            {
              title: 'web.integration.erp-code',
              dataIndex: 'erp_code',
              key: 'erp_code',
              render: (erp_code: string, field: any) => (
                <RenderIntegracaoModal
                  erp_code={erp_code}
                  organization_id={organization_id}
                  request_id={field.id}
                />
              )
            }
          ]
        : []),
      {
        dataIndex: 'users',
        key: 'reason',
        render: (users: User[]) => {
          const reason = users?.[0]?.reason
          if ([undefined, '', -1].includes(reason?.reason_id)) {
            return <></>
          }
          return (
            <Typography.Text>
              {reason.code ? '' : `${reason.code} - `}
              {reason.name}
            </Typography.Text>
          )
        },
        title: 'web.resources.reasons.title'
      },
      {
        title: 'web.table.head.users',
        dataIndex: 'users',
        key: 'users',
        render: (users: User[]) => (
          <Popover
            content={
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                <List
                  bordered={false}
                  dataSource={users}
                  itemLayout="horizontal"
                  renderItem={(item: User) => (
                    <ListItem>
                      <ListItemMeta
                        avatar={
                          <UserAvatar
                            gender={item.gender}
                            name={item.name}
                            size="large"
                          />
                        }
                        description={<UnifiedTag status={item.status} />}
                        title={item.name}
                      />
                    </ListItem>
                  )}
                />
              </div>
            }
            placement="bottomLeft"
          >
            <div>
              <RenderUsers users={users} />
            </div>
          </Popover>
        )
      },
      ...(myRequests
        ? [
            {
              title: 'web.table.head.empowered-users',
              dataIndex: 'empowered_users',
              key: 'empowered_users',
              render: (empowered_users: RequestEmpoweredUsers[] = []) => (
                <Popover
                  content={
                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                      <List
                        bordered={false}
                        dataSource={empowered_users}
                        itemLayout="horizontal"
                        renderItem={({
                          gender,
                          name,
                          requester,
                          role,
                          traveler
                        }: RequestEmpoweredUsers) => (
                          <ListItem>
                            <ListItemMeta
                              avatar={
                                <UserAvatar
                                  gender={gender}
                                  name={name}
                                  size="large"
                                />
                              }
                              description={
                                <>
                                  {role && <TagRole role={role} />}
                                  {traveler && <TagRole role="TRAVELER" />}
                                  {requester && <TagRole role="REQUESTER" />}
                                </>
                              }
                              title={name}
                            />
                          </ListItem>
                        )}
                      />
                    </div>
                  }
                  placement="bottomLeft"
                >
                  <div>
                    <RenderUsers users={empowered_users} />
                  </div>
                </Popover>
              )
            }
          ]
        : []),
      ...(approvals
        ? [
            {
              title: 'web.table.head.approvers',
              dataIndex: 'approvers',
              key: 'approvers',
              render: (approvers: Approver[]) => (
                <Popover
                  content={
                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                      <List
                        bordered={false}
                        dataSource={approvers}
                        itemLayout="horizontal"
                        renderItem={(item: Approver) => (
                          <ListItem>
                            <ListItemMeta
                              avatar={
                                <UserAvatar
                                  gender={item.approver.gender}
                                  name={item.approver.name}
                                  size="large"
                                />
                              }
                              description={
                                <>
                                  <UnifiedTag status={item.status} />
                                  <UnifiedTag status={item.approved_by} />
                                </>
                              }
                              title={item.approver.name}
                            />
                          </ListItem>
                        )}
                      />
                    </div>
                  }
                  placement="bottomLeft"
                >
                  <div>
                    <RenderUsers users={approvers.map(item => item.approver)} />
                  </div>
                </Popover>
              )
            }
          ]
        : []),
      ...(conferences
        ? [
            {
              title: 'web.table.head.checkers',
              dataIndex: 'checkers',
              key: 'checkers',
              render: (checkers: Checker[]) => (
                <Popover
                  content={
                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                      <List
                        bordered={false}
                        dataSource={checkers}
                        itemLayout="horizontal"
                        renderItem={(item: Checker) => (
                          <ListItem>
                            <ListItemMeta
                              avatar={
                                <UserAvatar
                                  gender={item.checker.gender}
                                  name={item.checker.name}
                                  size="large"
                                />
                              }
                              description={
                                <>
                                  <UnifiedTag status={item.status} />
                                  <UnifiedTag status={item.conferred_by} />
                                </>
                              }
                              title={item.checker.name}
                            />
                          </ListItem>
                        )}
                      />
                    </div>
                  }
                  placement="bottomLeft"
                >
                  <div>
                    <RenderUsers users={checkers.map(item => item.checker)} />
                  </div>
                </Popover>
              )
            }
          ]
        : []),
      ...(closings
        ? [
            {
              title: 'web.table.head.controllers',
              dataIndex: 'controllers',
              key: 'controllers',
              render: (controllers: Controller[]) => (
                <Popover
                  content={
                    <div style={{ maxHeight: 300, overflow: 'auto' }}>
                      <List
                        bordered={false}
                        dataSource={controllers}
                        itemLayout="horizontal"
                        renderItem={(item: Controller) => (
                          <ListItem>
                            <ListItemMeta
                              avatar={
                                <UserAvatar
                                  gender={item.controller.gender}
                                  name={item.controller.name}
                                  size="large"
                                />
                              }
                              description={
                                <>
                                  <UnifiedTag status={item.status} />
                                  <UnifiedTag status={item.closed_by} />
                                </>
                              }
                              title={item.controller.name}
                            />
                          </ListItem>
                        )}
                      />
                    </div>
                  }
                  placement="bottomLeft"
                >
                  <div>
                    <RenderUsers
                      users={controllers.map(item => item.controller)}
                    />
                  </div>
                </Popover>
              )
            }
          ]
        : []),
      {
        title: 'web.table.head.start-date',
        dataIndex: 'startDate',
        key: 'startDate'
      },
      {
        align: 'right',
        title: 'web.table.head.cost',
        dataIndex: 'cost',
        key: 'cost'
      },
      {
        align: 'center',
        dataIndex: 'status',
        key: 'status',
        render: (status: RequestUserDataStatus, props: any) => (
          <Css.Status>
            <SyncStatusBadge status={props?.sync_status} />
            <Css.Div />
            <UnifiedTag status={status} />
          </Css.Status>
        ),
        title: 'web.table.head.status'
      },
      ...(canShowExcludeRequest
        ? [
            {
              key: 'exclude',
              width: 10,
              render: (_: any) =>
                _.status !== 'FINISHED' && (
                  <ExcluirRequest
                    organization_id={organization_id}
                    request_id={_.id}
                  />
                )
            }
          ]
        : [])
    ],
    [
      approvals,
      canShowExcludeRequest,
      closings,
      conferences,
      myRequests,
      organization_id,
      tenant_id
    ]
  )

  return (
    <Css.Table
      className="table-striped"
      columns={columns}
      data-test-id="request-list"
      dataSource={dataSource}
      loading={loading}
      onChange={({ current }) => onChange(current)}
      // @ts-ignore
      onRow={onRow}
      pagination={{
        current: currentPage,
        pageSize: 10,
        total: totalPages
      }}
    />
  )
}

export default Table
